import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroImg from "@/assets/blog/magnesium-foods-india.webp";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/magnesium-rich-foods-india";
const TITLE = "15 Magnesium-Rich Foods in the Indian Diet (2026) | TruWe";

export const Route = createFileRoute("/blog/magnesium-rich-foods-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "magnesium-rich-foods-india",
      title: TITLE,
      description:
        "Foods high in magnesium, ranked for the Indian kitchen, pumpkin seeds, palak, almonds, ragi, rajma and more, with per-serving mg and a realistic daily plan.",
      ogTitle: "15 Magnesium-Rich Foods in the Indian Diet",
      ogDescription:
        "A complete guide to foods high in magnesium in India, per-serving amounts, absorption blockers, and how much a real thali actually delivers.",
      headline: "15 Magnesium-Rich Foods in the Indian Diet: A Complete Guide",
      breadcrumbName: "Magnesium-Rich Foods (India)",
      datePublished: "2026-08-02",
      dateModified: "2026-08-02",
      ogImage: `https://truwe.in${heroImg}`,
      faqs: [
        {
          q: "Which food has the most magnesium in the Indian diet?",
          a: "Pumpkin seeds (kaddu ke beej) are the densest everyday source, roughly 150 mg of magnesium in a 30 g handful, close to half an adult's daily requirement. Among staples, ragi, bajra, rajma and palak are the biggest contributors.",
        },
        {
          q: "How much magnesium do I need per day?",
          a: "The ICMR-NIN recommended intake for Indian adults is about 370 mg/day for men and 310 mg/day for women, rising in pregnancy. Most Indian dietary surveys put actual intake well below this.",
        },
        {
          q: "Can I get enough magnesium from food alone?",
          a: "It is possible but demanding. It typically takes seeds, nuts, a millet, a dal and a leafy vegetable every single day. Polished rice and maida-based meals deliver very little, which is why many people stay short despite eating plenty of vegetables.",
        },
        {
          q: "What blocks magnesium absorption from Indian food?",
          a: "Phytates in unsoaked grains and legumes, very high calcium loads taken at the same time, excess tea or coffee with meals, and long-term antacid (PPI) use all reduce how much magnesium you actually absorb. Soaking, sprouting and fermenting improve it.",
        },
        {
          q: "Is a magnesium supplement better than food?",
          a: "Neither replaces the other. Food gives you fibre, potassium and polyphenols alongside magnesium; a well-formulated supplement closes a measured gap using absorbable forms rather than magnesium oxide. Use food first and supplement the shortfall.",
        },
      ],
    }),
});

type FoodRow = {
  name: string;
  serving: string;
  mg: string;
  note: string;
};

const FOODS: FoodRow[] = [
  {
    name: "Pumpkin seeds (kaddu ke beej)",
    serving: "30 g (2 tbsp)",
    mg: "~150 mg",
    note: "The single densest source in an Indian pantry. Roast lightly and add to chaas, poha or salads.",
  },
  {
    name: "Chia seeds",
    serving: "30 g",
    mg: "~110 mg",
    note: "Also brings soluble fibre. Soak before eating, it improves both texture and mineral availability.",
  },
  {
    name: "Almonds (badam)",
    serving: "30 g (~23 nuts)",
    mg: "~80 mg",
    note: "Soaking overnight and slipping the skins reduces phytate load, the traditional Indian practice.",
  },
  {
    name: "Cashews (kaju)",
    serving: "30 g",
    mg: "~82 mg",
    note: "Higher in magnesium than most people assume, though calorie-dense, keep to a small handful.",
  },
  {
    name: "Peanuts / groundnut",
    serving: "30 g",
    mg: "~50 mg",
    note: "The most affordable nut-class source in India. Boiled or roasted, not fried in reused oil.",
  },
  {
    name: "Spinach (palak), cooked",
    serving: "1 cup",
    mg: "~155 mg",
    note: "Very high on paper, but oxalates bind some of it. Still one of the best vegetable sources.",
  },
  {
    name: "Methi and other leafy greens",
    serving: "1 cup cooked",
    mg: "~60-90 mg",
    note: "Rotate greens rather than relying on palak alone, different oxalate and mineral profiles.",
  },
  {
    name: "Ragi (finger millet)",
    serving: "100 g flour",
    mg: "~137 mg",
    note: "Also the highest-calcium Indian grain. Ragi roti or ragi malt is an easy daily swap for maida.",
  },
  {
    name: "Bajra (pearl millet)",
    serving: "100 g",
    mg: "~137 mg",
    note: "A winter staple across Rajasthan, Gujarat and Maharashtra, and a genuine magnesium carrier.",
  },
  {
    name: "Jowar (sorghum)",
    serving: "100 g",
    mg: "~165 mg",
    note: "Swapping two wheat rotis a day for jowar meaningfully lifts weekly magnesium intake.",
  },
  {
    name: "Rajma (kidney beans)",
    serving: "1 cup cooked",
    mg: "~75 mg",
    note: "Soak 8 hours and discard the water, better digestion and better mineral availability.",
  },
  {
    name: "Chana / kabuli chana",
    serving: "1 cup cooked",
    mg: "~78 mg",
    note: "Sprouted chana is a strong breakfast addition; sprouting cuts phytates substantially.",
  },
  {
    name: "Moong dal",
    serving: "1 cup cooked",
    mg: "~97 mg",
    note: "The easiest dal to digest, which matters if bloating is what keeps you off legumes.",
  },
  {
    name: "Dark chocolate (≥70%)",
    serving: "30 g",
    mg: "~65 mg",
    note: "Genuinely useful, not a loophole. Keep the portion small and the sugar low.",
  },
  {
    name: "Banana",
    serving: "1 medium",
    mg: "~32 mg",
    note: "Modest per serving, but it is the fruit most Indians actually eat daily, it adds up.",
  },
];

const BLOCKERS = [
  {
    title: "Refined grains replaced millets",
    body: "Polishing rice and milling wheat into maida strips the magnesium-rich germ and bran. Two generations of that shift is the biggest single reason Indian magnesium intake fell.",
  },
  {
    title: "Phytates in unsoaked grains and dals",
    body: "Phytic acid binds magnesium in the gut. Soaking, sprouting and fermenting (idli, dosa, dhokla) release a meaningful share of it, which is exactly what traditional prep already did.",
  },
  {
    title: "Tea and coffee with every meal",
    body: "Tannins and the diuretic effect together reduce net mineral retention. Shifting chai to 45-60 minutes away from meals is a free improvement.",
  },
  {
    title: "Antacids, PPIs and diuretics",
    body: "Long-term proton-pump inhibitor use is a documented cause of low magnesium, and diuretics increase urinary loss. Both are very common in Indian adults over 40.",
  },
  {
    title: "Depleted soil",
    body: "Intensive farming with NPK-heavy fertiliser has reduced magnesium in produce. The same palak your grandmother ate is not the same palak by weight today.",
  },
];

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Magnesium
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            15 magnesium-rich foods in the Indian diet: a complete guide
          </h1>
          <BlogByline date="2026-08-02" readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Magnesium runs more than 300 enzyme reactions, every unit of usable
            energy, every muscle relaxation, every calm night. Indian adults need
            roughly <strong>310-370 mg a day</strong>, and most dietary surveys put us
            well under it. The fix starts in the kitchen. Here are the foods highest in
            magnesium that already exist in Indian cooking, what each serving actually
            delivers, and where food alone tends to fall short.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroImg}
              alt="Magnesium-rich Indian foods, pumpkin seeds, almonds, cashews, spinach, ragi, bajra, dark chocolate and rajma"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            How much magnesium do you actually need?
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Adult men:</strong> ~370 mg/day (ICMR-NIN reference intake).
            </li>
            <li>
              <strong>Adult women:</strong> ~310 mg/day, higher in pregnancy and
              lactation.
            </li>
            <li>
              <strong>Higher need:</strong> heavy training, chronic stress, high sweat
              loss, diabetes, and long-term antacid or diuretic use.
            </li>
          </ul>
          <p className="mt-4 text-base text-foreground/85">
            A plain North Indian day of two wheat rotis, dal, sabzi and rice lands
            around 200-250 mg. Not disastrous, but a persistent 100 mg/day gap over
            years is exactly how tissue stores quietly drain.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 15 highest-magnesium foods in an Indian kitchen
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Values are typical published food-composition figures and vary with
            variety, soil and cooking method.
          </p>
          <div className="mt-5 space-y-4">
            {FOODS.map((row, i) => (
              <div
                key={row.name}
                className="rounded-2xl border border-border bg-card p-5 shadow-sm"
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <Leaf className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">
                    {i + 1}. {row.name}
                  </h3>
                  <span className="ml-auto rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
                    {row.mg} / {row.serving}
                  </span>
                </div>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            A realistic 350 mg day (Indian meals, no special shopping)
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Breakfast -</strong> ragi porridge or two jowar rotis with sabzi.{" "}
              <em>~120 mg</em>
            </li>
            <li>
              <strong>Mid-morning -</strong> 30 g soaked almonds + a banana.{" "}
              <em>~110 mg</em>
            </li>
            <li>
              <strong>Lunch -</strong> moong dal, palak sabzi, one roti.{" "}
              <em>~120 mg</em>
            </li>
            <li>
              <strong>Evening -</strong> 2 tbsp roasted pumpkin seeds or 20 g dark
              chocolate. <em>~60-150 mg</em>
            </li>
          </ul>
          <p className="mt-4 text-base text-foreground/85">
            That clears the target. The honest problem is repeatability: this has to
            happen most days of most weeks, and travel, hostel food, canteen lunches and
            late nights break it quickly.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Why Indians stay short even while eating "healthy"
          </h2>
          <div className="mt-5 space-y-4">
            {BLOCKERS.map((b) => (
              <div
                key={b.title}
                className="rounded-2xl border border-border bg-card p-5 shadow-sm"
              >
                <h3 className="font-display text-lg font-semibold text-brand-deep">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-foreground/80">{b.body}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            Dietary magnesium vs. a supplement, what's actually different
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Food magnesium arrives with fibre, potassium and polyphenols, and absorption
            sits around 30-40% depending on the meal's phytate and calcium load. A
            supplement removes the variability: a known amount, in a known chemical
            form, at a known time.
          </p>
          <p className="mt-3 text-base text-foreground/85">
            The catch is that the form decides the outcome. Magnesium oxide, the
            cheapest and most common filler on Indian shelves, is poorly absorbed and
            mostly acts as a laxative. Chelated and mineral-complex forms behave very
            differently in the gut. If you want the detail, read our{" "}
            <a
              href="/blog/magnesium-glycinate-vs-citrate-india"
              className="font-semibold text-brand underline underline-offset-4"
            >
              India buyer's guide to magnesium forms
            </a>
            , or check the{" "}
            <a
              href="/blog/magnesium-deficiency-symptoms-india"
              className="font-semibold text-brand underline underline-offset-4"
            >
              nine signs of low magnesium
            </a>{" "}
            first.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Mag Absorb Pro, for the gap food doesn't close
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              A blend of <strong>Aquamin® marine magnesium, magnesium bisglycinate,
              chloride and aspartate</strong> chosen for absorption rather than cost.
              No oxide filler, research-backed doses, third-party tested, made in a
              USFDA / GMP / FSSAI facility. Use it alongside the foods above, not
              instead of them.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Four absorbable forms" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Mag Absorb Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="magnesium" />
          <ClusterLinks cluster="magnesium" />
          <AuthorityFlow cluster="magnesium" />
          <References slug="magnesium-rich-foods-india" />
          <RelatedReading articles={relatedArticles("magnesium-rich-foods-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only, not medical advice or a diagnosis. Anyone with kidney
            disease should discuss magnesium intake with their doctor before
            supplementing.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
